import { Inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import {
  PartnerHealthInterface,
  PartnerHealthsService,
  PartnerHealthState,
  PartnerHealthSubState,
} from '@galaxy/partner';
import { AppConfigService } from '../../app-config.service';
import { FailedPaymentsDialogComponent } from './failed-payments-dialog.component';

@Injectable({ providedIn: 'root' })
export class FailedPaymentsService {
  private isClosed = true;
  isSuspended = false;

  constructor(
    private router: Router,
    private failedPaymentDialog: MatDialogRef<FailedPaymentsDialogComponent>,
    private dialog: MatDialog,
    private appConfigService: AppConfigService,
    private partnerHealthService: PartnerHealthsService,
    @Inject('PARTNER_ID') private partnerId$: Observable<string>,
  ) {
    combineLatest([this.pageChanged$, this.appConfigService.config$, this.partnerHealth$])
      .pipe(
        filter(([pageChanged, _, __]) => pageChanged),
        tap(([_, appConfig, partnerHealth]) => {
          if (appConfig.isSuperAdmin) {
            return;
          }
          if (this.isPartnerSubscriptionSuspended(partnerHealth)) {
            this.isSuspended = true;
            this.router.navigateByUrl('/failed-payments/restricted');
            return;
          }
          if (this.isClosed && this.isPartnerSubscriptionOverdue(partnerHealth)) {
            this.openDialog();
          }
        }),
      )
      .subscribe();
  }

  private isPartnerSubscriptionOverdue(partnerHealth: PartnerHealthInterface): boolean {
    return (
      partnerHealth.state === PartnerHealthState.PARTNER_HEALTH_STATE_AT_RISK &&
      partnerHealth.subState === PartnerHealthSubState.PARTNER_HEALTH_SUB_STATE_SUBSCRIPTION_OVERDUE
    );
  }

  private isPartnerSubscriptionSuspended(partnerHealth: PartnerHealthInterface): boolean {
    return (
      partnerHealth.state === PartnerHealthState.PARTNER_HEALTH_STATE_RESTRICTED &&
      partnerHealth.subState === PartnerHealthSubState.PARTNER_HEALTH_SUB_STATE_SUBSCRIPTION_SUSPENDED
    );
  }

  get pageChanged$(): Observable<boolean> {
    return this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e: NavigationEnd) => this.urlChecker(e)),
    );
  }

  get partnerHealth$(): Observable<PartnerHealthInterface> {
    return this.partnerId$.pipe(switchMap((partnerId) => this.partnerHealthService.get(partnerId)));
  }

  urlChecker(e: NavigationEnd): boolean {
    return !(e.url.startsWith('/billing') || e.url.startsWith('/superadmin') || e.url.startsWith('/failed-payments'));
  }

  goToBillingByPurchaseWithFilter(status: string): void {
    this.router.navigate(['/billing', 'by-purchase'], {
      queryParams: {
        'purchase-list': JSON.stringify({
          purchaseId: null,
          start: null,
          status: status,
          end: null,
          minPrice: null,
        }),
      },
    });
  }

  openDialog(): void {
    this.isClosed = false;
    const dialogRef = this.dialog.open(FailedPaymentsDialogComponent, {
      width: '400px',
      data: {},
    });

    dialogRef.beforeClosed().subscribe((result) => {
      if (result) {
        this.goToBillingByPurchaseWithFilter('Overdue');
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.isClosed = true;
    });
  }
}
